import React from "react";
import { useDataAccess } from "./useDataAccess";
import { DataAccessDenied } from "./DataAccessDenied";
import type { DataStream } from "@utils/data_access";

export type DataAccessGuardProps = {
  /** Single stream to gate on. Use `streams` for views that draw on several. */
  stream?: DataStream;
  streams?: DataStream[];
  /**
   * "all" (default) denies unless every stream is permitted — for a view that
   * renders all of them. "any" permits as soon as one is, for a view that shows
   * whichever streams the user can see (a player profile, say).
   */
  mode?: "all" | "any";
  /** Rendered while permissions load, to avoid flashing a denial at a permitted user. */
  loadingFallback?: React.ReactNode;
  variant?: "page" | "inline";
  children: React.ReactNode;
};

/**
 * Renders `children` only when the viewer may access the given data stream(s),
 * and the shared denial state otherwise.
 */
export const DataAccessGuard = ({
  stream,
  streams,
  mode = "all",
  loadingFallback = null,
  variant = "page",
  children,
}: DataAccessGuardProps) => {
  const { ready, canAccess } = useDataAccess();

  const required = streams ?? (stream ? [stream] : []);

  if (!ready) return <>{loadingFallback}</>;
  if (required.length === 0) return <>{children}</>;

  const permitted =
    mode === "any" ? required.some(canAccess) : required.every(canAccess);

  if (permitted) return <>{children}</>;

  const missing = required.filter((s) => !canAccess(s));

  return <DataAccessDenied streams={missing} variant={variant} />;
};

export default DataAccessGuard;
